// Das Fundbuch — der offene Hinweis und was schon geborgen ist.
//
// Ohne diese Seite ist ein Hinweis nach dem Schließen des Panels verloren:
// „18 500 Meter nach Westen" merkt sich niemand über zwei Sitzungen, und ein
// Fortschritt, den nur das Gedächtnis des Spielers trägt, ist keiner.
//
// Der Inhalt liegt als **reine Funktionen, die HTML liefern**, hier und nicht
// als Template-Block im Panel — dieselbe Aufteilung wie bei `ui/help.ts`. Ohne
// DOM ist er prüfbar, und es gibt bewusst kein jsdom.

import { balancing, enemy, resource, zonesFor } from '../data/balancing';
import type { Cache } from '../data/types';
import { distStr, escapeHtml, fmt } from '../core/format';
import { resIconSvg } from '../gfx/icons';
import { cacheHint, cachesFor, cacheFound, openCache, type CacheSave } from '../sim/caches';
import { gearFor, gearHint, hasGear } from '../sim/gear';
import { guardianHint } from '../sim/hostiles';

/**
 * Was die Seite liest — der Spielstand plus die größte Entfernung.
 *
 * `deepest` steht daneben und nicht in `CacheSave`, weil es kein Kettenstand
 * ist, sondern ein Statistikzähler: er entscheidet nur, **ob** der Spieler von
 * der Spur überhaupt weiß.
 */
export interface FindsProgress extends CacheSave {
  deepest: number;
  seed: number;
  /**
   * Gebaute Ausbaustufen — sie entscheiden, **ob es den Wächter überhaupt gibt**.
   *
   * Steht daneben und nicht in `CacheSave`, aus demselben Grund wie `deepest`:
   * kein Kettenstand, sondern ein Riegel für das, was die Seite am Ende zeigt.
   */
  built: number;
  /**
   * Abschüsse je Gegnerart — dieselbe Karte, die auch der Technologiebaum liest.
   *
   * Als ganze Karte und nicht als `guardianDown: boolean`, wortgleich zur
   * Begründung bei `treeStatus`: der Aufrufer reicht die eine Karte herein, und
   * die Entscheidung „wer zählt als erlegt" bleibt dort, wo die Wächter-ID aus
   * `balancing()` kommt. Sonst stünde sie im Panel.
   */
  kills: Readonly<Record<string, number>>;
}

/**
 * Was am Ende der Spur steht — **eine** Auswertung für Inhalt und Schlüssel.
 *
 * Dieselbe Bauform wie `nodeState`: zwei Aufrufer, eine Regel. Zwei getrennte
 * Abfragen wären zwei Wahrheiten, und die Seite zeigte irgendwann etwas
 * anderes, als ihr Änderungsschlüssel abbildet.
 */
export type GuardianStage = 'spur' | 'sperre' | 'offen' | 'erlegt';

export function guardianStage(progress: FindsProgress): GuardianStage {
  if (openCache(progress) !== null) return 'spur';
  const kind = enemy(balancing().guardian.id);
  // Kein `throw`, wenn die Zeile fehlt: die Tabellen kommen vom Server, und
  // einer, der dem Client voraus ist, darf keinen Browser stolpern lassen —
  // dieselbe Begründung wie bei `guardian_kind()` im Backend. Dann bleibt es
  // beim alten Text.
  if (!kind || progress.built < kind.minBuild) return 'sperre';
  return (progress.kills[kind.id] ?? 0) > 0 ? 'erlegt' : 'offen';
}

/**
 * Weiß der Spieler überhaupt von der Spur?
 *
 * Erst wer den tiefen Raum erreicht hat, hat die erste Peilnotiz aufgeschnappt.
 * Vorher gäbe es nichts zu zeigen, und **ein Reiter, der nichts zeigt, ist ein
 * Versprechen ohne Einlösung** — dieselbe Regel wie beim Booster-Reiter, der
 * erst mit dem ersten Kristall erscheint.
 *
 * Sie steht nur im Client: der Server braucht sie nicht, denn sie sperrt keinen
 * Fund, sondern nur die Anzeige. Wer zufällig über eine Kiste fliegt, findet sie
 * auch ohne — und das ist der bessere Entdeckungsmoment.
 */
export function findsRevealed(progress: FindsProgress): boolean {
  const zonen = zonesFor(progress.era);
  const tief = zonen[zonen.length - 1].from;
  return progress.deepest >= tief || cachesFor(progress).some((c) => cacheFound(progress, c));
}

/**
 * Der Änderungsschlüssel der Seite.
 *
 * Er bildet ab, **was die Seite zeigt**: welche Kisten geborgen sind, welcher
 * Hinweis offen ist und ob die Spur überhaupt bekannt ist. Die größte
 * Entfernung geht als **Schwelle** ein und nicht als Zahl — sie wächst beim
 * Hinausfliegen laufend, und eine laufende Zahl im Schlüssel zieht dem Spieler
 * die Liste unter dem Finger weg.
 */
export function findsListKey(progress: FindsProgress): string {
  const offen = openCache(progress);
  return [
    progress.era,
    findsRevealed(progress) ? '1' : '0',
    offen ? offen.id : '-',
    cachesFor(progress).filter((c) => cacheFound(progress, c)).map((c) => c.id).join('|'),
    // Der Arm gehört in den Schlüssel: mit ihm verschwindet der Warnsatz am
    // offenen Hinweis, und ein Abschnitt wechselt von „liegt draußen" auf
    // „am Schiff". Ohne ihn bliebe die Seite stehen, bis sich zufällig etwas
    // anderes ändert.
    gearFor(progress).filter((g) => hasGear(progress, g.id)).map((g) => g.id).join('|'),
    // Der Wächterzustand geht als **Stufe** ein und nicht als Zahl: die gebauten
    // Stufen wachsen, die Zahl seiner Fälle auch, und die Seite zeigt von beidem
    // nur die Schwelle. Roh im Schlüssel wären beide feiner als das Gezeigte —
    // genau die teurere Hälfte der Regel, bei der die Liste im Bildtakt ersetzt
    // wird und ein Klick nie ankommt.
    guardianStage(progress),
  ].join('§');
}

function rewardHtml(cache: Cache): string {
  return Object.entries(cache.reward)
    .map(([id, amount]) => {
      const res = resource(id);
      return `${res ? resIconSvg(res, 14) : ''} ${fmt(amount)} `
        + `${escapeHtml(res ? res.name : id)}`;
    })
    .join(' · ');
}

/**
 * Der Abschnitt über die Fundstücke.
 *
 * Er steht **zwischen** offenem Hinweis und geborgenen Funden, und das ist die
 * Reihenfolge der Fragen: „wo muss ich hin", „was brauche ich dafür", „was hatte
 * ich schon".
 *
 * Ein Gerät, das man hat, behält seinen Eintrag — er sagt dann, was es kann.
 * Ihn verschwinden zu lassen wäre die Antwort auf „wieso kann ich den Schrank
 * jetzt heben?" schuldig geblieben.
 */
function gearHtml(progress: FindsProgress): string {
  const liste = gearFor(progress);
  if (!liste.length) return '';
  return liste
    .map((gear) => {
      const da = hasGear(progress, gear.id);
      const kopf = `<h3>${escapeHtml(gear.name)}${da ? ' — am Schiff' : ''}</h3>`;
      const wo = da
        ? '<p class="tt-eff">Montiert. Schwere Funde lassen sich damit aufnehmen.</p>'
        : `<p><b>${escapeHtml(gearHint(gear, progress.seed, progress.era))}</b> — dort treibt er. `
          + `Bis auf ${distStr(balancing().gearWorld.reach)} heran, dann hast du ihn.</p>`;
      return `<div class="chapter ${da ? 'probe' : 'locked'}">${kopf}`
        + `<p>${escapeHtml(gear.desc)}</p>${wo}</div>`;
    })
    .join('');
}

/**
 * Was am Ende der Spur steht.
 *
 * Der frühere Zweig lautete „Die Spur endet. Du hast alles gefunden" — eine
 * Sackgasse an genau der Stelle, an der die Übergabe stehen müsste: der Wächter
 * steht per Konstruktion **hinter der Mitte derselben Kette** (`guardian.band`
 * ist ein Anteil davon), und an seinem Fall hängt der ganze Wächterkern-Ast.
 *
 * Der Ton trägt, dass hier **kein Fund** wartet. Ein Eintrag im Stil der Kisten
 * schickte jemanden mit derselben Erwartung los, mit der er die letzte Kiste
 * geholt hat — und ins offene Feuer.
 */
function guardianHtml(progress: FindsProgress): string {
  const stufe = guardianStage(progress);
  if (stufe === 'spur') return '';

  const ende = '<div class="chapter"><h3>Die Spur endet</h3><p>Du hast alles gefunden, was '
    + 'hier draußen versteckt wurde.';
  const kind = enemy(balancing().guardian.id);
  if (stufe === 'sperre' || !kind) {
    // Ein Hinweis auf etwas, das es noch nicht gibt, ist eine Lüge: unter
    // `minBuild` steht an seinem Punkt nichts, ohne jede Meldung.
    return `${ende} Die letzte Notiz hat eine Rückseite, und die ist leer geblieben.</p>`
      + (kind
        ? `<p class="tt-lock">Noch. Ab ${kind.minBuild} gebauten Ausbaustufen steht im tiefen `
          + 'Raum etwas, das vorher nicht da war.</p>'
        : '')
      + '</div>';
  }

  const peilung = escapeHtml(guardianHint(progress.seed, progress.era));
  const name = escapeHtml(kind.name);
  if (stufe === 'erlegt') {
    // Der Eintrag bleibt stehen und wechselt nur den Ton: der Wächterkern-Ast
    // kostet Kerne je Knoten, also ist das keine einmalige Wegbeschreibung,
    // sondern eine **Adresse**.
    return `<div class="chapter probe"><h3>Am Ende der Spur: ${name}</h3>`
      + `<p><b>${peilung}</b> — dort steht er wieder. Er kommt zurück, und er kommt an `
      + 'dieselbe Stelle; weggezogen hat ihn immer nur, wer nah genug war.</p></div>';
  }

  return '<div class="chapter probe"><h3>Die Spur endet — und etwas steht am Ende</h3>'
    + '<p>Du hast alles gefunden, was hier draußen versteckt wurde. Die letzte Notiz nennt '
    + 'keine Kiste mehr, sondern eine Peilung und den Satz, der bei allen anderen fehlt: '
    + 'nicht anfliegen.</p>'
    + `<p><b>${peilung}</b> — dort steht ${name}. Er bleibt nicht immer dort: wer ihm zu nahe `
    + 'kommt, zieht ihn mit. Aber er kehrt an diese Stelle zurück.</p>'
    + `<p class="tt-lock">Hier wartet kein Fund. Ab ${escapeHtml(distStr(kind.reach))} bist du `
    + 'in seinem Wachbereich, und er schießt zurück — er hält mehr aus als alles andere, was '
    + 'hier draußen steht.</p></div>';
}

/**
 * Die ganze Seite.
 *
 * Sie zeigt **den offenen Hinweis oben** und die geborgenen Funde darunter. Die
 * Reihenfolge ist Absicht: die Frage, mit der jemand das Panel öffnet, lautet
 * „wo muss ich hin", nicht „was hatte ich schon".
 */
export function findsHtml(progress: FindsProgress): string {
  if (!findsRevealed(progress)) return '';

  const offen = openCache(progress);
  const gefunden = cachesFor(progress).filter((cache) => cacheFound(progress, cache));
  const kopf = '<div class="chapter"><h3>Die Spur</h3><p>Irgendjemand hat hier draußen '
    + 'Vorräte versteckt und die Wege dazwischen aufgeschrieben. Auf der Karte steht davon '
    + 'nichts — es gibt nur die Peilung und das Suchen.</p></div>';

  const hinweis = offen
    ? `<div class="chapter probe"><h3>Offener Hinweis: ${escapeHtml(offen.name)}</h3>`
      + `<p><b>${escapeHtml(cacheHint(offen, progress.seed, progress.era))}</b> — dort treibt sie. `
      + `Bis auf ${distStr(balancing().cache.reach)} heran — ab ${distStr(balancing().cache.pulseRange)} `
      + 'siehst du sie pulsieren.</p>'
      + (hasGear(progress, offen.needsGear)
        ? ''
        : '<p class="tt-eff">Sie ist zu groß, um sie von Hand zu nehmen — ohne den '
          + 'Verladearm stehst du davor und kommst nicht weiter.</p>')
      + '</div>'
    : guardianHtml(progress);

  const funde = gefunden.length
    ? `<div class="chapter locked"><h3>Geborgen (${gefunden.length})</h3>`
      + '<p>Was in den Kisten lag — und was daneben.</p></div>'
      + gefunden
        .map((cache) => `<div class="chapter probe"><h3>${escapeHtml(cache.name)}</h3>`
          + `<p>${escapeHtml(cache.desc)}</p>`
          + `<p class="tt-eff">${rewardHtml(cache)}</p></div>`)
        .join('')
    : '';

  return kopf + hinweis + gearHtml(progress) + funde;
}
